import { useMemo } from 'react'
import cx from 'classnames'
import { IconButton, PageSection } from '@/common'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/hooks/use18n.ts'
import { MethodEmissionReport } from '@/api/EmissionReportModel'
import { HeatFlowSystems, ProductionRoute, StoredCO2Handling } from '@/features/production-routes/api/ProductionRouteModel'
import { PrecursorMaterialWithSupplier } from '@/api/PrecursorMaterialModel'
import HeatSection from './heat/HeatSection.tsx'
import ProcessEmissionsSection, {
  AssetWithMetadata,
  EmissionTypeToReport
} from './process/ProcessEmissionsSection.tsx'
import WasteGasSection from './wasteGas/WasteGasSection.tsx'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import MassBalanceSection from './massBalance/MassBalanceSection.tsx'
import { MethodCalculatedEmission } from '@/shared/api/types'
import LocalizedNumberFlow from '@/components/LocalizednNumberFlow'
import StoredCO2Section from './storedCO2/StoredCO2Section'
import ActivityLevelValidationSection from './activityLevelValidation/ActivityLevelValidationSection'
import { uniq } from 'es-toolkit'
import { CaretDownIcon } from '@/common/components/icons/index.ts'

export function isMethodEmissionReportComplete(payload: {
  methodEmissionReport: MethodEmissionReport
  productionRoute: ProductionRoute
  precursors: PrecursorMaterialWithSupplier[]
  precursorAggGoodCategory: string[]
  usesMassBalance: boolean
  isApplicableForActivityLevelValidation: boolean
  isPrecursorRequired: boolean
  emissionsToReport?: EmissionTypeToReport[]
}) {
  const {
    methodEmissionReport,
    productionRoute,
    precursors,
    precursorAggGoodCategory,
    usesMassBalance,
    isApplicableForActivityLevelValidation,
    isPrecursorRequired,
    emissionsToReport
  } = payload
  const {
    heatEmissionRaw,
    wasteGasImportedEmissionRaw,
    wasteGasExportedEmissionRaw,
    materialOutput,
    storedCO2AmountTonnes,
    electricity,
    activityLevelValidation
  } = methodEmissionReport

  const isMaterialOutputComplete = Boolean(
    materialOutput?.every(item => {
      return item.carbonContent.quantity > 0
    })
  )

  const { heatFlowSystem, electricityProductionMethods, storedCO2Handling, isUsingWasteGas } =
    productionRoute
  const isElectricityComplete = !!electricity

  // Since the form validates that the entire heat section is filled, we can just check if the heatEmissionRaw is not null
  const isHeatComplete = !!heatEmissionRaw

  const isHeatWithinReportScope = heatFlowSystem !== HeatFlowSystems.None
  const isWasteGasWithinReportScope = isUsingWasteGas
  const isActivityLevelValidationPending = activityLevelValidation?.hasToBeReEvaluated
  const isActivityLevelValidationComplete =
    Boolean(activityLevelValidation) && !isActivityLevelValidationPending
  const arePrecursorsComplete = precursors.length > 0 || !isPrecursorRequired
  const isStoredCo2WithinReportScope = storedCO2Handling !== StoredCO2Handling.None
  const isElectricityWithinReportScope = electricityProductionMethods.length > 0
  const arePrecursorsWithinReportScope = precursorAggGoodCategory.length > 0
  const isWasteGasComplete = !!wasteGasImportedEmissionRaw && !!wasteGasExportedEmissionRaw

  const productionMethodHasAssets = productionRoute.assets.length > 0

  const combustionAssetsToReport =
    emissionsToReport?.filter(emission => emission.emissionType === 'COMBUSTION') || []
  const co2AssetsToReport =
    emissionsToReport?.filter(emission => emission.emissionType === 'CO2') || []
  const pfcAssetsToReport =
    emissionsToReport?.filter(emission => emission.emissionType === 'PFC') || []

  const areCombustionAssetsComplete =
    combustionAssetsToReport.length > 0
      ? combustionAssetsToReport.every(
          emission =>
            typeof emission.emissionRecord?.emission === 'number' &&
            emission.emissionRecord.emission >= 0
        )
      : true

  const areCo2AssetsComplete =
    co2AssetsToReport.length > 0
      ? co2AssetsToReport.every(
          emission => emission.emissionRecord && emission.emissionRecord.emission >= 0
        )
      : true

  const arePfcAssetsComplete =
    pfcAssetsToReport.length > 0
      ? pfcAssetsToReport.every(
          emission => emission.emissionRecord && emission.emissionRecord.emission >= 0
        )
      : true

  const isStoredCo2Complete = storedCO2AmountTonnes || storedCO2AmountTonnes === 0 ? true : false

  const isMassBalanceComplete = isMaterialOutputComplete
  const completion = [
    {
      id: 'storedCO2',
      isWithinReportScope: isStoredCo2WithinReportScope,
      complete: isStoredCo2Complete,
      isOptional: false
    },
    {
      id: 'heat',
      isWithinReportScope: isHeatWithinReportScope,
      complete: isHeatComplete
    },
    {
      id: 'massBalance',
      isWithinReportScope: usesMassBalance,
      complete: isMaterialOutputComplete
    },
    {
      id: 'activityLevelValidation',
      isWithinReportScope: isApplicableForActivityLevelValidation,
      complete: isActivityLevelValidationComplete
    },
    {
      id: 'precursors',
      isWithinReportScope: arePrecursorsWithinReportScope,
      complete: arePrecursorsComplete
    },
    {
      id: 'assets',
      isWithinReportScope: productionMethodHasAssets,
      complete: areCombustionAssetsComplete && areCo2AssetsComplete && arePfcAssetsComplete
    },
    {
      id: 'wasteGas',
      isWithinReportScope: isWasteGasWithinReportScope,
      complete: isWasteGasComplete
    },
    {
      id: 'electricity',
      isWithinReportScope: isElectricityWithinReportScope,
      complete: isElectricityComplete,
      isOptional: false
    }
  ]

  const sectionsWithinReportScope = completion.filter(item => item.isWithinReportScope)
  const completeSections = sectionsWithinReportScope.filter(item => item.complete)
  const isComplete = completeSections.length === sectionsWithinReportScope.length
  const percentageComplete = (completeSections.length / sectionsWithinReportScope.length) * 100

  return {
    isHeatComplete,
    isElectricityComplete,
    isHeatWithinReportScope,
    isWasteGasWithinReportScope,
    arePrecursorsComplete,
    isStoredCo2WithinReportScope,
    isStoredCo2Complete,
    isElectricityWithinReportScope,
    isActivityLevelValidationComplete,
    isActivityLevelValidationPending,
    arePrecursorsWithinReportScope,
    isWasteGasComplete,
    areCombustionAssetsComplete,
    areCo2AssetsComplete,
    arePfcAssetsComplete,
    isMassBalanceComplete,
    isComplete,
    percentageComplete,
    completion,
    isMassBalanceWithinReportScope: usesMassBalance
  }
}

export default function MethodReport({
  isViewMode,
  calculatedEmission,
  index,
  isOpen,
  toggle,
  methodEmissionReport,
  reportId,
  nextMethodEmissionReportId,
  productionRoute,
  isIntegratedProcess,
  hasAnyPrecursor
}: {
  isViewMode: boolean
  methodEmissionReport: MethodEmissionReport
  calculatedEmission?: MethodCalculatedEmission
  index: number
  isOpen: boolean
  toggle: () => void
  reportId: string
  nextMethodEmissionReportId: string | null
  productionRoute: ProductionRoute
  isIntegratedProcess: boolean
  hasAnyPrecursor: boolean
}) {
  const { t } = useI18n()

  const { productionMethodsByCode, assetsById } = useAppConfig().config

  const { productionMethodCode, id: processId, emissionsRaw } = methodEmissionReport

  const productionMethodDefinition = productionMethodsByCode[productionMethodCode]
  const isPrecursorRequired = productionMethodDefinition?.isPrecursorRequired
  const usesMassBalance = Boolean(productionMethodDefinition?.usesMassBalance)
  const isApplicableForActivityLevelValidation = Boolean(
    productionMethodDefinition?.isApplicableForActivityLevelValidation
  )

  const precursorAggGoodCategory = productionMethodDefinition?.precursorAggGoodCategory || []

  const assetsToReport: AssetWithMetadata[] = productionRoute.assets.map(productionRouteAsset => {
    const { assetDefinitionId } = productionRouteAsset
    const assetDefinition = assetDefinitionId ? assetsById[assetDefinitionId] : null
    const emissionRecords = emissionsRaw
      ? emissionsRaw.filter(er => er.asset.id === productionRouteAsset.id)
      : []
    return {
      productionRouteAsset,
      assetDefinition,
      emissionRecords
    }
  })

  const emissionsToReport: EmissionTypeToReport[] = useMemo(() => {
    return assetsToReport.flatMap(asset => {
      const { assetDefinition, productionRouteAsset, emissionRecords } = asset

      const emissionTypes = productionRouteAsset.usesFuel
        ? uniq([...(assetDefinition?.emissionTypes || []), 'COMBUSTION'])
        : [...(assetDefinition?.emissionTypes || [])]

      const methods = productionRouteAsset.usesFuel
        ? uniq([...(assetDefinition?.calculationMethods || []), 'COMBUSTION'])
        : [...(assetDefinition?.calculationMethods || [])]

      return emissionTypes.map(emissionType => {
        const methodsForEmissionType = methods.filter(method => {
          const emisionTypesFromMethod = method.split('.')[0]
          return emisionTypesFromMethod === emissionType
        })

        const emissionRecord = emissionRecords.find(er => er.emissionType === emissionType) || null
        return {
          emissionType,
          possibleMethods: methodsForEmissionType,
          productionRouteAsset,
          assetDefinition,
          emissionRecord
        }
      })
    })
  }, [assetsToReport])

  const {
    isHeatComplete,
    isHeatWithinReportScope,
    isWasteGasWithinReportScope,
    isWasteGasComplete,
    areCombustionAssetsComplete,
    areCo2AssetsComplete,
    arePfcAssetsComplete,
    isMassBalanceComplete,
    isMassBalanceWithinReportScope,
    isActivityLevelValidationComplete,
    isStoredCo2Complete,
    isStoredCo2WithinReportScope,
    isActivityLevelValidationPending
  } = isMethodEmissionReportComplete({
    emissionsToReport,
    methodEmissionReport,
    productionRoute,
    precursors: [],
    precursorAggGoodCategory,
    usesMassBalance,
    isApplicableForActivityLevelValidation,
    isPrecursorRequired
  })

  const activityLevel = calculatedEmission?.activityLevel
  const totalDirectEmissions = calculatedEmission?.totalDirectEmissions || 0

  return (
    <>
      <PageSection
        title={
          !isIntegratedProcess &&
          t(`common:productionMethods.${methodEmissionReport.productionMethodCode}`)
        }
        actions={
          !isIntegratedProcess && (
            <div className="flex items-center gap-2">
              <div className="text-sm text-text-secondary">
                <LocalizedNumberFlow value={totalDirectEmissions} /> {UnitLabel.tCO2}
              </div>
              <IconButton
                onClick={toggle}
                variant="txt-default"
                icon={
                  <CaretDownIcon
                    className={`transition-transform duration-150 ease-linear origin-center ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                }
              />
            </div>
          )
        }
      >
        <div
          className={cx('transition-all duration-300 overflow-hidden ease-in space-y-4', {
            'h-calc-auto opacity-100': isOpen,
            'h-0 opacity-0': !isOpen
          })}
        >
          {isMassBalanceWithinReportScope && (
            <MassBalanceSection
              reportId={reportId}
              isViewMode={isViewMode}
              isComplete={isMassBalanceComplete}
              methodEmissionReport={methodEmissionReport}
              calculatedEmission={calculatedEmission}
              nextMethodEmissionReportId={nextMethodEmissionReportId}
              index={index}
            />
          )}
          {productionRoute.assets.length > 0 && (
            <ProcessEmissionsSection
              reportId={reportId}
              processId={processId}
              emissionsRaw={methodEmissionReport.emissionsRaw || []}
              emissionsToReport={emissionsToReport}
              isViewMode={isViewMode}
              areCombustionAssetsComplete={areCombustionAssetsComplete}
              areCo2AssetsComplete={areCo2AssetsComplete}
              arePfcAssetsComplete={arePfcAssetsComplete}
              activityLevel={activityLevel || 0}
            />
          )}
          {isHeatWithinReportScope && (
            <HeatSection
              processId={methodEmissionReport.id}
              emissions={calculatedEmission?.netHeatEmissions || 0}
              reportId={reportId}
              isViewMode={isViewMode}
              isComplete={isHeatComplete}
              heatEmissionRaw={methodEmissionReport.heatEmissionRaw || null}
            />
          )}
          {isWasteGasWithinReportScope && (
            <WasteGasSection
              processId={methodEmissionReport.id}
              reportId={reportId}
              emissions={calculatedEmission?.netWasteGasEmissions || 0}
              isViewMode={isViewMode}
              isComplete={isWasteGasComplete}
              wasteGasImportedEmission={methodEmissionReport.wasteGasImportedEmissionRaw || null}
              wasteGasExportedEmission={methodEmissionReport.wasteGasExportedEmissionRaw || null}
            />
          )}
          {isStoredCo2WithinReportScope && (
            <StoredCO2Section
              reportId={reportId}
              processId={processId}
              isViewMode={isViewMode}
              isComplete={isStoredCo2Complete}
              storedCO2AmountTonnes={methodEmissionReport.storedCO2AmountTonnes}
              storedCO2Handling={productionRoute.storedCO2Handling}
            />
          )}
          {isApplicableForActivityLevelValidation && (
            <ActivityLevelValidationSection
              hasAnyPrecursor={hasAnyPrecursor}
              reportId={reportId}
              processId={processId}
              isViewMode={isViewMode}
              activityLevelValidation={methodEmissionReport.activityLevelValidation || null}
              activityLevel={activityLevel || 0}
              isComplete={isActivityLevelValidationComplete || false}
              isPending={isActivityLevelValidationPending}
            />
          )}
        </div>
      </PageSection>
    </>
  )
}
